/**
 * Onboard modal: choose connection method for owner wallet.
 * 1) Mnemonic: enter mnemonic, derive ETH address at index 0 (no password) via mormSigner
 * 2) Wallet extension: detect installed wallet; greyed out when no wallet detected
 * Uses detectSupportedWallet and connect patterns from AgentDelegation.
 */
import { useState, useCallback, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useConnect } from "wagmi";
import { isAddress } from "viem";
import { detectSupportedWallet } from "../hooks/useAgentDelegation";
import mormSigner from "../sdk/signing/mormSigner";

const WALLET_DISPLAY_NAMES: Record<string, string> = {
  phantom: "Phantom",
  metamask: "MetaMask",
  onekey: "OneKey",
};

export interface OnboardModalProps {
  isOpen: boolean;
  /** Called when mnemonic is valid; should return a Promise that resolves on success, rejects on failure. Modal stays open until resolved. */
  onMnemonicConnect: (mnemonic: string, address: string) => void | Promise<void>;
  onWalletExtensionConnect: (address: string) => void;
  onClose?: () => void;
}

interface EthRequestProvider {
  request(args: { method: "eth_requestAccounts" }): Promise<string[]>;
}

export function OnboardModal({
  isOpen,
  onMnemonicConnect,
  onWalletExtensionConnect,
  onClose,
}: OnboardModalProps): JSX.Element | null {
  const [mnemonic, setMnemonic] = useState("");
  const [mnemonicError, setMnemonicError] = useState<string | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setMnemonic("");
      setMnemonicError(null);
      setWalletError(null);
    }
  }, [isOpen]);

  const { connect, connectors, isPending } = useConnect();
  const injectedConnector = connectors.find((c) => c.id === "injected");

  const supportedWallet = useMemo(() => detectSupportedWallet(), []);
  const isWalletDetected = !!supportedWallet;
  const walletDisplayName = supportedWallet
    ? WALLET_DISPLAY_NAMES[supportedWallet] ?? "Browser Wallet"
    : "";

  const deriveAddressFromMnemonic = useCallback(
    async (phrase: string): Promise<string | null> => {
      const trimmed = phrase.trim();
      if (!trimmed) return null;
      try {
        if (!mormSigner.isReady()) {
          await mormSigner.initialize();
        }
        const result = await mormSigner.generateAddress(trimmed, 0);
        return result.success && result.address ? result.address : null;
      } catch {
        return null;
      }
    },
    [],
  );

  const handleMnemonicConnect = useCallback(async () => {
    setIsConnecting(true);
    setMnemonicError(null);
    const trimmed = mnemonic.trim();
    if (!trimmed) {
      setMnemonicError("Please enter a mnemonic phrase");
      setIsConnecting(false);
      return;
    }
    try {
      const address = await deriveAddressFromMnemonic(trimmed);
      if (!address) {
        setMnemonicError("Invalid mnemonic phrase");
        return;
      }
      await Promise.resolve(onMnemonicConnect(trimmed, address));
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Connection failed";
      setMnemonicError(msg);
    } finally {
      setIsConnecting(false);
    }
  }, [mnemonic, deriveAddressFromMnemonic, onMnemonicConnect]);

  const handleWalletExtensionConnect = useCallback(async () => {
    if (!injectedConnector || !supportedWallet) return;
    const w = typeof window !== "undefined" ? window : null;
    if (!w) return;

    setIsConnecting(true);
    setWalletError(null);
    const ethereum = w.ethereum;
    let provider: EthRequestProvider | null = ethereum as EthRequestProvider | null;
    if (supportedWallet === "phantom") {
      const phantomEth = w.phantom?.ethereum;
      const phantomSelf =
        w.phantom && "request" in w.phantom && typeof (w.phantom as EthRequestProvider).request === "function"
          ? (w.phantom as EthRequestProvider)
          : null;
      provider =
        (phantomEth as EthRequestProvider) ??
        phantomSelf ??
        (ethereum?.isPhantom ? (ethereum as EthRequestProvider) : null);
    } else if (supportedWallet === "onekey") {
      provider = (w.$onekey?.ethereum ?? ethereum) as EthRequestProvider | null;
    }

    if (!provider || typeof provider.request !== "function") {
      setIsConnecting(false);
      return;
    }

    try {
      const accounts = await provider.request({ method: "eth_requestAccounts" });
      if (!accounts?.length || !accounts[0]) {
        setWalletError(
          "No accounts returned. Please approve the connection in your wallet.",
        );
        return;
      }
      const address = accounts[0];
      if (!isAddress(address)) {
        setWalletError("Invalid address returned from wallet");
        return;
      }
      await connect({ connector: injectedConnector });
      await new Promise((resolve) => setTimeout(resolve, 300));
      onWalletExtensionConnect(address);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to connect wallet";
      setWalletError(msg);
      console.error("Wallet connect error:", err);
    } finally {
      setIsConnecting(false);
    }
  }, [connect, injectedConnector, supportedWallet, onWalletExtensionConnect]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="modal-overlay onboard-modal-overlay"
      onClick={onClose ?? (() => {})}
      role="presentation"
    >
      <div
        className="modal-content onboard-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="onboard-modal-title"
      >
        <div className="modal-header">
          <h3 id="onboard-modal-title">Connect Wallet</h3>
          <p className="onboard-modal-subtitle">
            Choose how to connect as the owner
          </p>
        </div>

        <div className="onboard-modal-body">
          {/* Option 1: Mnemonic */}
          <div className="onboard-option">
            <h4 className="onboard-option-title">1. Mnemonic phrase</h4>
            <p className="onboard-option-desc">
              Enter your mnemonic. Address derived at index 0 (ETH, no password).
            </p>
            <textarea
              className="onboard-mnemonic-input"
              value={mnemonic}
              onChange={(e) => {
                setMnemonic(e.target.value);
                setMnemonicError(null);
              }}
              placeholder="Enter your 12 or 24 word mnemonic phrase..."
              rows={3}
              disabled={isConnecting}
              autoComplete="off"
            />
            {mnemonicError && (
              <div className="onboard-error">{mnemonicError}</div>
            )}
            <button
              type="button"
              className="btn btn-primary onboard-option-btn"
              onClick={handleMnemonicConnect}
              disabled={isConnecting || !mnemonic.trim()}
            >
              {isConnecting ? "Connecting..." : "Connect with Mnemonic"}
            </button>
          </div>

          {/* Option 2: Wallet extension - greyed when no wallet detected */}
          <div
            className={`onboard-option ${!isWalletDetected ? "onboard-option-greyed" : ""}`}
          >
            <h4 className="onboard-option-title">2. Wallet extension</h4>
            <p className="onboard-option-desc">
              {isWalletDetected
                ? `${walletDisplayName} detected`
                : "No wallet extension detected"}
            </p>
            {walletError && (
              <div className="onboard-error">{walletError}</div>
            )}
            <button
              type="button"
              className="btn btn-secondary onboard-option-btn"
              onClick={handleWalletExtensionConnect}
              disabled={!isWalletDetected || isConnecting || isPending}
            >
              {isPending || isConnecting
                ? "Connecting..."
                : isWalletDetected
                  ? `Connect with ${walletDisplayName}`
                  : "No wallet available"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
}
